import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import type { Unsubscribe } from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
  Notebook,
  NotebookPage,
  CreateNotebookInput,
  UpdateNotebookInput,
  CreatePageInput,
  UpdatePageInput,
  InviteToNotebookInput,
} from '@/types/notebook';
import type { SharedUser } from '@/types/thinkspace';
import type { User } from 'firebase/auth';

const COLLECTION = 'notebooks';

export const notebooksService = {
  // ============================================
  // Basic CRUD Operations
  // ============================================

  // Get a single Notebook by ID
  getById: async (id: string): Promise<Notebook | null> => {
    const docRef = doc(db, COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as Notebook;
  },

  // Get all Notebooks owned by user
  getOwned: async (userId: string): Promise<Notebook[]> => {
    const q = query(
      collection(db, COLLECTION),
      where('ownerId', '==', userId),
      orderBy('updatedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Notebook);
  },

  // Get all Notebooks shared with user (accepted invitations only)
  getSharedWithMe: async (userEmail: string): Promise<Notebook[]> => {
    const allNotebooks = await getDocs(collection(db, COLLECTION));
    return allNotebooks.docs
      .map((d) => ({ id: d.id, ...d.data() }) as Notebook)
      .filter((notebook) =>
        notebook.sharedWith?.some(
          (share) => share.email.toLowerCase() === userEmail.toLowerCase() && share.status === 'accepted'
        )
      );
  },

  // Get pending invitations for user
  getPendingInvitations: async (userEmail: string): Promise<Notebook[]> => {
    const allNotebooks = await getDocs(collection(db, COLLECTION));
    return allNotebooks.docs
      .map((d) => ({ id: d.id, ...d.data() }) as Notebook)
      .filter((notebook) =>
        notebook.sharedWith?.some(
          (share) => share.email.toLowerCase() === userEmail.toLowerCase() && share.status === 'pending'
        )
      );
  },

  // Subscribe to a Notebook for real-time updates
  subscribe: (
    id: string,
    callback: (notebook: Notebook | null) => void,
    onError?: (error: Error) => void
  ): Unsubscribe => {
    const docRef = doc(db, COLLECTION, id);
    return onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          callback(null);
          return;
        }
        callback({ id: snapshot.id, ...snapshot.data() } as Notebook);
      },
      onError
    );
  },

  // Create a new Notebook
  create: async (input: CreateNotebookInput, user: User): Promise<string> => {
    const docRef = doc(collection(db, COLLECTION));

    // Create first page with default template
    const firstPage: NotebookPage = {
      id: crypto.randomUUID(),
      order: 0,
      template: input.defaultTemplate || 'ruled-college',
      canvasData: '{}', // Empty canvas
      thumbnail: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const notebook: Omit<Notebook, 'id'> = {
      name: input.name,
      description: input.description || '',
      ownerId: user.uid,
      ownerEmail: user.email || '',
      ownerName: user.displayName || '',
      paperSize: input.paperSize || 'a4',
      defaultTemplate: input.defaultTemplate || 'ruled-college',
      pages: [firstPage],
      sharedWith: [],
      coverImage: null,
      createdAt: serverTimestamp() as unknown as Timestamp,
      updatedAt: serverTimestamp() as unknown as Timestamp,
    };

    await setDoc(docRef, notebook);
    return docRef.id;
  },

  // Update Notebook metadata
  update: async (
    id: string,
    data: UpdateNotebookInput
  ): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete a Notebook
  delete: async (id: string): Promise<void> => {
    const docRef = doc(db, COLLECTION, id);
    await deleteDoc(docRef);
  },

  // ============================================
  // Page Operations
  // ============================================

  // Add a new page
  addPage: async (id: string, input: CreatePageInput = {}): Promise<NotebookPage> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    // Calculate order - either after specific page or at the end
    let newOrder: number;
    if (input.insertAfterPageId) {
      const afterPage = notebook.pages.find((p) => p.id === input.insertAfterPageId);
      if (afterPage) {
        // Shift all pages after this one
        const updatedPages = notebook.pages.map((p) => {
          if (p.order > afterPage.order) {
            return { ...p, order: p.order + 1 };
          }
          return p;
        });
        newOrder = afterPage.order + 1;
        notebook.pages = updatedPages;
      } else {
        newOrder = Math.max(...notebook.pages.map((p) => p.order), -1) + 1;
      }
    } else {
      newOrder = Math.max(...notebook.pages.map((p) => p.order), -1) + 1;
    }

    const newPage: NotebookPage = {
      id: crypto.randomUUID(),
      order: newOrder,
      template: input.template || notebook.defaultTemplate,
      canvasData: '{}',
      thumbnail: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      pages: [...notebook.pages, newPage],
      updatedAt: serverTimestamp(),
    });

    return newPage;
  },

  // Update a page (canvas data, thumbnail, template)
  updatePage: async (
    id: string,
    pageId: string,
    updates: UpdatePageInput
  ): Promise<void> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    const updatedPages = notebook.pages.map((page) => {
      if (page.id === pageId) {
        return {
          ...page,
          ...updates,
          updatedAt: Timestamp.now(),
        };
      }
      return page;
    });

    // Update cover image if first page thumbnail changes
    let coverUpdate = {};
    const firstPage = updatedPages.reduce((min, p) =>
      p.order < min.order ? p : min, updatedPages[0]);
    if (firstPage?.id === pageId && updates.thumbnail) {
      coverUpdate = { coverImage: updates.thumbnail };
    }

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      pages: updatedPages,
      ...coverUpdate,
      updatedAt: serverTimestamp(),
    });
  },

  // Delete a page
  deletePage: async (id: string, pageId: string): Promise<void> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    // Prevent deleting the last page
    if (notebook.pages.length <= 1) {
      throw new Error('Cannot delete the last page');
    }

    const pageToDelete = notebook.pages.find((p) => p.id === pageId);
    if (!pageToDelete) throw new Error('Page not found');

    // Remove page and reorder remaining pages
    const updatedPages = notebook.pages
      .filter((page) => page.id !== pageId)
      .map((page) => {
        if (page.order > pageToDelete.order) {
          return { ...page, order: page.order - 1 };
        }
        return page;
      });

    // Update cover if first page was deleted
    const newFirstPage = updatedPages.reduce((min, p) =>
      p.order < min.order ? p : min, updatedPages[0]);

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      pages: updatedPages,
      coverImage: newFirstPage?.thumbnail || null,
      updatedAt: serverTimestamp(),
    });
  },

  // Reorder pages (after drag-and-drop)
  reorderPages: async (id: string, pageIds: string[]): Promise<void> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    const orderMap = new Map(pageIds.map((pageId, index) => [pageId, index]));

    const updatedPages = notebook.pages.map((page) => ({
      ...page,
      order: orderMap.get(page.id) ?? page.order,
    }));

    // Update cover image based on new first page
    const newFirstPage = updatedPages.reduce((min, p) =>
      p.order < min.order ? p : min, updatedPages[0]);

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      pages: updatedPages,
      coverImage: newFirstPage?.thumbnail || null,
      updatedAt: serverTimestamp(),
    });
  },

  // ============================================
  // Sharing Operations (View-Only)
  // ============================================

  // Invite a user to view the notebook
  inviteUser: async (
    id: string,
    input: InviteToNotebookInput,
    invitedBy: string
  ): Promise<void> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    // Check if already shared
    const existingShare = notebook.sharedWith.find(
      (s) => s.email.toLowerCase() === input.email.toLowerCase()
    );
    if (existingShare) {
      throw new Error('User already invited');
    }

    const sharedUser: SharedUser = {
      id: crypto.randomUUID(),
      email: input.email.toLowerCase(),
      displayName: null,
      photoURL: null,
      permission: 'read', // Notebooks are view-only
      status: 'pending',
      invitedAt: Timestamp.now(),
      respondedAt: null,
      invitedBy,
    };

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: [...notebook.sharedWith, sharedUser],
      updatedAt: serverTimestamp(),
    });
  },

  // Accept or decline invitation
  respondToInvitation: async (
    id: string,
    userEmail: string,
    accept: boolean,
    user: User
  ): Promise<void> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    const updatedSharedWith = notebook.sharedWith.map((share) => {
      if (share.email.toLowerCase() === userEmail.toLowerCase()) {
        return {
          ...share,
          status: accept ? 'accepted' : 'declined',
          respondedAt: Timestamp.now(),
          displayName: user.displayName,
          photoURL: user.photoURL,
        };
      }
      return share;
    });

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Remove shared user
  removeSharedUser: async (id: string, userEmail: string): Promise<void> => {
    const notebook = await notebooksService.getById(id);
    if (!notebook) throw new Error('Notebook not found');

    const updatedSharedWith = notebook.sharedWith.filter(
      (share) => share.email.toLowerCase() !== userEmail.toLowerCase()
    );

    const docRef = doc(db, COLLECTION, id);
    await updateDoc(docRef, {
      sharedWith: updatedSharedWith,
      updatedAt: serverTimestamp(),
    });
  },

  // Check if user has access (view-only for shared users)
  checkAccess: (
    notebook: Notebook,
    userId: string,
    userEmail: string
  ): { hasAccess: boolean; canEdit: boolean; isOwner: boolean } => {
    // Owner has full access
    if (notebook.ownerId === userId) {
      return { hasAccess: true, canEdit: true, isOwner: true };
    }

    // Shared users only have view access
    const share = notebook.sharedWith.find(
      (s) => s.email.toLowerCase() === userEmail.toLowerCase() && s.status === 'accepted'
    );

    if (share) {
      return {
        hasAccess: true,
        canEdit: false, // Notebooks are view-only for shared users
        isOwner: false,
      };
    }

    return { hasAccess: false, canEdit: false, isOwner: false };
  },
};
